import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { canSeePrices } from '../utils/permissions';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import { formatDate, formatDateShort, formatDateTime, formatDateLong } from '../utils/dateUtils';
import {
  getJob, updateJob, updateJobStatus, deleteJob,
  createJobDescription, updateJobDescription, deleteJobDescription,
  updateJobDescriptionStatus, bookJobDescription,
  createJobItem, updateJobItem, deleteJobItem,
  createInvoice, getJobVariance, getJobInvoicePreview,
  getWorkersDropdown,
  uploadAttachment, getAttachmentsByEntity, downloadAttachment, downloadThumbnail, deleteAttachment,
  createComment, downloadJobPDF, downloadJobSheetWord, addJobAssignee, removeJobAssignee,
  rescheduleJobDescription, getSchedulesForEntity,
  updateSchedule, deleteSchedule
} from '../services/api';
import {
  ArrowLeft, Loader2, Plus, Edit, Trash2, X, CheckCircle,
  Briefcase, Receipt, Calendar, Upload, Download,
  Paperclip, MessageCircle, Send, User, AlertCircle, Eye, Camera,
  RefreshCw, FileText, TrendingUp
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { CommentsSection } from '../components/CommentsSection';

import { formatDescriptionHtml, statusLabels, statusBtnLabel, typeLabels } from '../components/jobs/JobHelpers';
import { BookScheduleModal, SetOngoingModal, SetCompletedModal } from '../components/jobs/ScheduleModals';
import { EditDescriptionModal } from '../components/jobs/EditDescriptionModal';
import { AddAssigneeModal, RescheduleModal } from '../components/jobs/AssigneeModals';
import { ActualItemRow, AddItemForm } from '../components/jobs/ItemComponents';
import { VarianceModal } from '../components/jobs/VarianceModal';
import { InvoicePreviewModal } from '../components/jobs/InvoicePreviewModal';
import { JobAttachmentsModal } from '../components/jobs/JobAttachmentsModal';
import { CostBuilderModal } from '../components/quotes/CostBuilderModal';
import { usePageTitle } from '../hooks/usePageTitle';

function JobDetail() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const showPrices = canSeePrices(currentUser);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showVariance, setShowVariance] = useState(false);
  const [selectedDesc, setSelectedDesc] = useState(null);
  const [showEditDesc, setShowEditDesc] = useState(false);
  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleAssignee, setRescheduleAssignee] = useState(null);
  const [showBookSchedule, setShowBookSchedule] = useState(false);
  const [showSetOngoing, setShowSetOngoing] = useState(false);
  const [showSetCompleted, setShowSetCompleted] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [expandedDesc, setExpandedDesc] = useState(null);
  const [showJobAttachments, setShowJobAttachments] = useState(false);
  const [attachmentDescId, setAttachmentDescId] = useState(null);
  const [showJobBuilder, setShowJobBuilder] = useState(false);
  const [builderDescId, setBuilderDescId] = useState(null);

  const { data: job, isLoading, refetch } = useQuery({
    queryKey: ['job', id], queryFn: () => getJob(id).then(r => r.data)
  });

  const { data: varianceData, refetch: refetchVariance } = useQuery({
    queryKey: ['jobVariance', id], queryFn: () => getJobVariance(id).then(r => r.data), enabled: !!id
  });

  usePageTitle(job?.job_number);

  const updateDescMutation = useMutation({
    mutationFn: ({ descId, data }) => updateJobDescription(descId, data),
    onSuccess: () => { refetch(); setShowEditDesc(false); toast.success('Description updated'); }
  });

  const updateDescStatusMutation = useMutation({
    mutationFn: ({ descId, data }) => updateJobDescriptionStatus(descId, data),
    onSuccess: (_, vars) => {
      refetch(); refetchVariance();
      if (vars.data.status === 2) { setShowSetOngoing(false); toast.success('Job status has been changed to on going'); }
      if (vars.data.status === 3) { setShowSetCompleted(false); toast.success('Job status has been changed to Completed'); }
    }
  });

  const bookDescMutation = useMutation({
    mutationFn: ({ descId, data }) => bookJobDescription(descId, data),
    onSuccess: () => { refetch(); setShowBookSchedule(false); setShowAddAssignee(false); toast.success('Schedule booked successfully'); }
  });

  const createInvoiceMutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (res) => {
      toast.success(`Invoice created`);
      setShowInvoicePreview(false);
      navigate(`/invoices/${res.data.invoice_id || res.data.id}`);
    }
  });

  // Job-item mutations powering the Job Builder modal (mirrors Quote item flow).
  const addJobItemMutation = useMutation({
    mutationFn: createJobItem,
    onSuccess: () => { refetchVariance(); toast.success('Item added'); },
    onError: () => toast.error('Failed to add item'),
  });
  const updateJobItemMutation = useMutation({
    mutationFn: ({ id: itemId, data }) => updateJobItem(itemId, data),
    onSuccess: () => { refetchVariance(); },
    onError: () => toast.error('Failed to update item'),
  });
  const deleteJobItemMutation = useMutation({
    mutationFn: (itemId) => deleteJobItem(itemId),
    onSuccess: () => { refetchVariance(); toast.success('Item removed'); },
    onError: () => toast.error('Failed to remove item'),
  });

  // Map variance items -> Cost Builder existingItems shape for the active description
  const builderExistingItems = (varianceData?.items || [])
    .filter(i => i.job_description_id === builderDescId && i.job_item_id)
    .map(i => ({
      id: i.job_item_id,
      item_type: i.item_type,
      item_name: i.item_name,
      uom: i.actual_uom || i.quoted_uom,
      qty: i.quoted_qty,
      cost: i.planned_cost ?? i.actual_cost,
      sell_price: i.quoted_cost,
      markup: i.markup || 0,
      total_cost: i.quoted_total,
    }));

  // formatDate and formatDateLong imported from utils/dateUtils

  // Status action handler - routes to correct modal
  const handleStatusAction = (desc) => {
    setSelectedDesc(desc);
    if (desc.status === 0) setShowBookSchedule(true);
    else if (desc.status === 1) setShowSetOngoing(true);
    else if (desc.status === 2) setShowSetCompleted(true);
  };

  const handleBookSchedule = (formData) => {
    bookDescMutation.mutate({
      descId: selectedDesc.id,
      data: {
        start_date: formData.start_date, end_date: formData.end_date, remarks: formData.remarks,
        assignees: [{ assignee_id: formData.assignee_id, start_time: formData.start_time, end_time: formData.end_time }]
      }
    });
  };

  const handleAddAssignee = (formData) => {
    bookDescMutation.mutate({
      descId: selectedDesc.id,
      data: {
        start_date: formData.start_date || job?.date_start,
        end_date: formData.end_date || job?.date_end,
        remarks: '',
        assignees: [{ assignee_id: formData.assignee_id, start_time: formData.start_time, end_time: formData.end_time }]
      }
    });
  };

  const handleReschedule = async (scheduleId, formData) => {
    try {
      await updateSchedule(scheduleId, formData);
      toast.success('Schedule updated');
      setShowReschedule(false);
      setRescheduleAssignee(null);
      refetch();
    } catch (err) {
      toast.error('Failed to update schedule');
    }
  };

  const handleRemoveAssignee = async (scheduleIds, name) => {
    if (!window.confirm(`Remove ${name} from this job?`)) return;
    try {
      const ids = Array.isArray(scheduleIds) ? scheduleIds : [scheduleIds];
      for (const sid of ids) {
        await deleteSchedule(sid);
      }
      toast.success(`${name} removed`);
      refetch();
    } catch (err) {
      toast.error('Failed to remove assignee');
    }
  };

  const handleCreateInvoice = (descriptions) => {
    createInvoiceMutation.mutate({
      job_id: job.id, client_id: job.client_id, property_address_id: job.property_address_id,
      quote_id: job.quote_id, is_deposit: false,
      descriptions
    });
  };

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;

  const status = statusLabels[job?.status] || statusLabels['0'];
  const isSingleLine = job?.descriptions?.length === 1;
  const hasQuote = !!job?.quote_id;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="job-detail-page">
      {/* Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/jobs" className="text-slate-600 hover:text-slate-800" data-testid="back-to-jobs"><ArrowLeft size={20} /></Link>
          <span className="text-slate-500">Job No:</span>
          <span className="text-2xl font-bold text-primary" data-testid="job-number">{job?.job_number}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {job?.has_invoice && <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Invoiced</span>}
          {!job?.has_invoice && showPrices && (
            <button onClick={() => { setShowInvoicePreview(true); }} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1" data-testid="create-invoice-btn">
              <Receipt size={14} /> Create Invoice
            </button>
          )}
          {showPrices && (
            <button
              onClick={async () => {
                try {
                  const res = await downloadJobSheetWord(id);
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Job_Detail_${job?.job_number || 'export'}.docx`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error('Failed to generate Word document'); }
              }}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1"
              data-testid="download-word-btn"
            >
              <FileText size={14} /> Download Word
            </button>
          )}
        </div>
      </div>

      {/* Linked Invoices - hidden from Ops */}
      {showPrices && job?.linked_invoices?.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="linked-invoices-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <Receipt size={16} className="text-green-700" />
            <span className="font-semibold text-green-800 text-sm">Linked Invoice{job.linked_invoices.length > 1 ? 's' : ''}:</span>
            {job.linked_invoices.map(inv => (
              <Link key={inv.id} to={`/invoices/${inv.id}`} className={`inline-flex items-center gap-1 px-3 py-1 text-white text-sm rounded hover:opacity-90 transition-colors ${inv.is_deposit ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-green-600 hover:bg-green-700'}`} data-testid={`linked-invoice-${inv.invoice_number}`}>
                {inv.invoice_number}{inv.is_deposit ? ' (Deposit)' : ''}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Job Sheet Card */}
      <div className="flex justify-center">
        <div className="w-full max-w-5xl">
          <div className="bg-white border rounded-lg shadow-sm" style={{ minHeight: '50vh' }}>
            <div className="p-6">
              {/* JOB SHEET Title */}
              <div className="text-center text-3xl font-bold mb-6">JOB SHEET</div>

              {/* Info Grid - matching original layout */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
                {/* Left Column */}
                <div className="md:col-span-7">
                  <img src="/logo.png" alt="Logo" className="h-16 mb-4" />
                  <div className="space-y-2">
                    <div><span className="font-bold">Contact:</span> <span className="text-slate-500 text-sm ml-2">{job?.property_manager_phone || '-'}</span></div>
                    <div><span className="font-bold">Quote No.:</span> {job?.quote_id ? <Link to={`/quotes/${job.quote_id}`} className="text-primary hover:underline text-sm ml-2">{job?.quote_number}</Link> : <span className="text-slate-400 text-sm ml-2">-</span>}</div>
                  </div>
                </div>
                {/* Right Column */}
                <div className="md:col-span-5 space-y-2">
                  <div><span className="font-bold">Job Name:</span> <span className="text-slate-500 text-sm ml-2" data-testid="job-client-name">{job?.job_title || job?.descriptions?.[0]?.job_title || '-'}</span></div>
                  <div><span className="font-bold">Address:</span> {job?.property_address ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.property_address)}`} target="_blank" rel="noopener noreferrer" className="text-[#3498db] hover:underline text-sm ml-2" data-testid="job-address">{job.property_address}</a> : <span className="text-slate-500 text-sm ml-2" data-testid="job-address">-</span>}</div>
                  <div><span className="font-bold">Start Date:</span> <span className="text-slate-500 text-sm ml-2">{formatDate(job?.date_start)}</span></div>
                  <div><span className="font-bold">Start Time:</span> <span className="text-slate-500 text-sm ml-2">{job?.time_start || '-'}</span></div>
                  <div><span className="font-bold">Tenant:</span> <span className="text-slate-500 text-sm ml-2">{job?.tenant_name || '-'}</span></div>
                  <div><span className="font-bold">Tenant Contact No.:</span> <span className="text-slate-500 text-sm ml-2">{job?.tenant_phone || '-'}</span></div>
                </div>
              </div>

              <hr className="my-4" />

              {/* Job Descriptions Table */}
              <div className="space-y-0" data-testid="job-descriptions">
                {job?.descriptions?.map((desc) => {
                  const descStatus = statusLabels[desc.status] || statusLabels['0'];
                  return (
                    <div key={desc.id} className="border-t py-3" data-testid={`job-desc-${desc.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <b className="text-[15px]">{desc.job_title}</b>
                          </div>
                          {desc.description && (
                            <div className="text-sm text-slate-600 prose prose-sm max-w-none overflow-hidden break-words [word-break:break-word]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatDescriptionHtml(desc.description)) }} />
                          )}
                        </div>
                        {/* Assignees + Status on the right */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {desc.desc_assignees?.map((a, i) => (
                            <div key={a.schedule_id || `${a.id}-${i}`} className="flex items-center gap-2 text-xs whitespace-nowrap" data-testid={`desc-assignee-${a.id}`}>
                              <span className="font-bold text-orange-500 uppercase">{a.name}</span>
                              <span className="text-slate-500">{a.booked_info}</span>
                              <button
                                onClick={() => { setRescheduleAssignee(a); setShowReschedule(true); }}
                                className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-xs hover:bg-slate-300"
                                data-testid={`reschedule-${a.schedule_id}`}
                              >Re-Schedule</button>
                              <span className={`px-2 py-0.5 text-xs rounded ${descStatus.color}`}>{descStatus.label}</span>
                              <button
                                onClick={() => handleRemoveAssignee(a.schedule_ids || [a.schedule_id], a.name)}
                                className="px-1.5 py-0.5 text-red-500 hover:bg-red-50 rounded"
                                title="Remove assignee"
                                data-testid={`remove-assignee-${a.schedule_id}`}
                              ><X size={14} /></button>
                            </div>
                          ))}
                          {(!desc.desc_assignees || desc.desc_assignees.length === 0) && (
                            <span className={`px-3 py-1 text-xs rounded ${descStatus.color}`} data-testid={`desc-status-${desc.id}`}>{descStatus.label}</span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons - matching original */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => { setAttachmentDescId(desc.id); setShowJobAttachments(true); }} className="px-3 py-1.5 text-xs bg-slate-500 text-white rounded hover:bg-slate-600" data-testid={`attachments-${desc.id}`}>Attachments</button>
                        <button onClick={() => { setSelectedDesc(desc); setShowEditDesc(true); }} className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600" data-testid={`edit-desc-${desc.id}`}>Edit Description</button>
                        {showPrices && (
                          <button onClick={() => { setBuilderDescId(desc.id); refetchVariance(); setShowJobBuilder(true); }} className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center gap-1" data-testid={`build-job-${desc.id}`}>
                            <Plus size={12} /> Build Job
                          </button>
                        )}
                        {showPrices && (
                          <button onClick={() => { setSelectedDesc(desc); refetchVariance(); setShowVariance(true); }} className="px-3 py-1.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center gap-1" data-testid={`show-variance-${desc.id}`}>
                            <TrendingUp size={12} /> Show Variance
                          </button>
                        )}
                        {desc.status < 3 && (
                          <button onClick={() => handleStatusAction(desc)} className="px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600" data-testid={`status-action-${desc.id}`}>
                            {statusBtnLabel[desc.status] || ''}
                          </button>
                        )}
                        <button onClick={() => { setSelectedDesc(desc); setShowAddAssignee(true); }} className="px-3 py-1.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600" data-testid={`add-assignee-${desc.id}`}>Add Assignee</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === MODALS === */}
      <VarianceModal isOpen={showVariance} onClose={() => setShowVariance(false)} description={selectedDesc} varianceData={varianceData} onRefresh={refetchVariance} jobId={id} hasQuote={hasQuote} />
      <EditDescriptionModal key={(selectedDesc?.id || 'new') + '-' + showEditDesc} isOpen={showEditDesc} onClose={() => setShowEditDesc(false)} description={selectedDesc} onSubmit={data => updateDescMutation.mutate({ descId: selectedDesc?.id, data })} loading={updateDescMutation.isPending} />
      <BookScheduleModal isOpen={showBookSchedule} onClose={() => setShowBookSchedule(false)} onSubmit={handleBookSchedule} loading={bookDescMutation.isPending} descTitle={selectedDesc?.job_title} />
      <SetOngoingModal isOpen={showSetOngoing} onClose={() => setShowSetOngoing(false)} onSubmit={() => updateDescStatusMutation.mutate({ descId: selectedDesc?.id, data: { status: 2 } })} loading={updateDescStatusMutation.isPending} descTitle={selectedDesc?.job_title} />
      <SetCompletedModal isOpen={showSetCompleted} onClose={() => setShowSetCompleted(false)} onSubmit={(date, time) => updateDescStatusMutation.mutate({ descId: selectedDesc?.id, data: { status: 3, completion_date: date, completion_time: time } })} loading={updateDescStatusMutation.isPending} descTitle={selectedDesc?.job_title} />
      <AddAssigneeModal isOpen={showAddAssignee} onClose={() => setShowAddAssignee(false)} onSubmit={handleAddAssignee} loading={bookDescMutation.isPending} />
      <RescheduleModal isOpen={showReschedule} onClose={() => { setShowReschedule(false); setRescheduleAssignee(null); }} onSubmit={handleReschedule} loading={false} assignee={rescheduleAssignee} />
      <InvoicePreviewModal isOpen={showInvoicePreview} onClose={() => setShowInvoicePreview(false)} job={job} onSubmit={handleCreateInvoice} loading={createInvoiceMutation.isPending} />
      <JobAttachmentsModal isOpen={showJobAttachments} onClose={() => setShowJobAttachments(false)} descriptionId={attachmentDescId} />

      {/* Job Builder — same as Quote builder, writes to job_items */}
      <CostBuilderModal
        isOpen={showJobBuilder}
        onClose={() => { setShowJobBuilder(false); setBuilderDescId(null); }}
        descriptionId={builderDescId}
        quoteId={null}
        existingItems={builderExistingItems}
        onAddItem={(data) => addJobItemMutation.mutate({
          job_description_id: data.quote_description_id,
          item_type: data.item_type,
          item_name: data.item_name,
          uom: data.uom,
          qty: data.qty,
          cost: data.cost,
          sell_price: data.sell_price,
        })}
        onRemoveItem={(itemId) => deleteJobItemMutation.mutate(itemId)}
        onUpdateItem={(itemId, data) => updateJobItemMutation.mutate({ id: itemId, data })}
        refetch={refetchVariance}
        readOnly={false}
      />

      {/* Comments */}
      <div className="mt-6">
        <CommentsSection entityType="job" entityId={id} />
      </div>
    </div>
  );
}

export default JobDetail;

